/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useEffect, useMemo, useState } from "react"
import { Form } from "grab-ui"
import {
  Row,
  Col,
  Button,
  Input,
  Divider,
  Spin,
  Tag,
  Drawer,
} from "@duxton-web/ui"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import { useLocaleContext } from "common/utils/i18n"
import {
  eventValueToCleanNumericInput,
  getAdvancedPricingRules,
  getValidationProps,
  hasFormFieldsErrors,
  newDeferredValidator,
  newPriceDiffValidator,
  stringValidations,
} from "utils/form_helper"
import { storageHelper } from "utils/storage"
import { getCurrencyInfo } from "utils/currency"
import { shallowEqual, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import {
  AdditionalPrice,
  getAdvancedPricingValidationConfig,
  getLocalisedName,
} from "constants/service_based_menu"
import useFEConfig from "hooks/useFEConfig"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { getCurrencyCodeByCountryCode } from "common/utils/locale"
import { getUserCountry } from "troy-common/utils"
import {
  drawerBodyStyle,
  drawerContentStyle,
  drawerFooter,
  drawerFooterSpacing,
} from "./styles"

interface AdditionalPriceDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(): void
  onAdditionalPriceChange: (additionalPrice: AdditionalPrice) => void
  initialValues?: AdditionalPrice
  validationType: "item" | "modifier"
  existingDefaultPrice: string | null | number
}

function CustomTag() {
  const textMap = useTranslationsMapTyped({
    custom: "menu.drawers.additionalPrice.labels.custom",
  })
  return (
    <Tag
      size="xsmall"
      css={css`
        margin-top: 4px;
        width: fit-content;
      `}
    >
      {textMap.custom}
    </Tag>
  )
}

function getFieldNameForInputFromConfig(
  pricingConfigKey: string,
  channelKey?: string,
) {
  const distinctServiceOrderKeys = pricingConfigKey.split("&")
  return distinctServiceOrderKeys
    .map(e => (!channelKey ? e : `${e}_${channelKey}`))
    .join("&")
}

export const AdditionalPriceDrawerComponent: FC<AdditionalPriceDrawerProps> = ({
  visible,
  onClose,
  onAdditionalPriceChange,
  initialValues,
  existingDefaultPrice,
  form,
  validationType,
}: AdditionalPriceDrawerProps) => {
  const { outletV2 } = useSelector(
    (state: AppState) => state?.menu,
    shallowEqual,
  )
  const feConfig = useFEConfig()
  const { disallowedSOCKeys } = feConfig
  const pricingConfig =
    feConfig.data?.serviceBasedMenuConfig?.characteristicStrategy?.pricingConfig
  const { isLoading } = feConfig

  const locale = useLocaleContext("locale")

  const [isEditedThroughAntd, setIsEditedThroughAntd] = useState(false) // i mean, seriously, who decided that setFieldsValue should reset isFieldsTouched??

  const advancedPricingValidationConfig = getAdvancedPricingValidationConfig(
    validationType,
    feConfig,
  )

  const textMap = useTranslationsMapTyped({
    title: "menu.drawers.item.changePriceByOrderType",
    pricePlaceholder: "menu.drawers.item.placeholders.price",
    errorRequired: "menu.errors.requiredField",
    errorPriceInvalid: "menu.errors.price.priceInvalid",
    confirm: "common.confirm",
    regular: "menu.drawers.item.labels.regularPrice",
    applyToAll: "common.applyToAll",
    errorPriceDifference: "menu.errors.advancedPricing.priceDifference",
    errorPriceDeviation: "menu.errors.advancedPricing.priceDeviation",
    errorPriceExceed: "menu.errors.price.exceedConfigPriceLimit",
  })

  const {
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    setFieldsValue,
    resetFields,
    validateFields,
  } = form

  useEffect(() => {
    if (visible && initialValues) {
      resetFields()
      setIsEditedThroughAntd(false)
      setFieldsValue({}) // hack to force re-render to display Custom tag, since getFieldDecorator's initialValue doesn't refresh the component apparently
      validateFields({ force: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isLoading, initialValues])

  const priceValidator =
    validationType === "item"
      ? newDeferredValidator(
          form,
          [
            {
              pattern: stringValidations.numericalAmount,
              message: textMap.errorPriceInvalid,
            },
          ],
          [
            {
              pattern: stringValidations.required,
              message: textMap.errorRequired,
            },
            {
              pattern: stringValidations.moreThanZeroDecimal,
              message: textMap.errorPriceInvalid,
            },
          ],
        )
      : newDeferredValidator(form, [
          {
            pattern: stringValidations.numericalAmountAllowZero,
            message: textMap.errorPriceInvalid,
          },
        ])
  const optionalPriceValidator = newDeferredValidator(form, [
    {
      pattern: stringValidations.numericalAmount,
      message: textMap.errorPriceInvalid,
    },
  ])

  // some initialValues keys may contain &, which are combined keys, so this splits them all up into individual keys
  // e.g. { "DineIn_OnDemand&DineIn_Scheduled": 100 } -> { "DineIn_OnDemand": 100, "DineIn_Scheduled": 100 }
  const splittedInitialValues = useMemo(
    () =>
      Object.entries(initialValues ?? {}).reduce<AdditionalPrice>(
        (acc, [key, val]) => {
          const keys = key.split("&")
          keys.forEach(k => {
            // eslint-disable-next-line no-param-reassign
            acc[k] = val
          })
          return acc
        },
        {
          regular: null,
        },
      ),
    [initialValues],
  )

  const findInitialValue = (fieldName: string): string | null | undefined => {
    const fieldNamesToLookup = fieldName.split("&")
    let initialValue: string | null | undefined
    for (let i = 0; i < fieldNamesToLookup.length; i += 1) {
      let fieldNameToLookup = fieldNamesToLookup[i]
      while (!initialValue) {
        const foundInitialValue = splittedInitialValues[fieldNameToLookup]
        if (foundInitialValue !== null && foundInitialValue !== undefined) {
          if (i > 1 && foundInitialValue !== initialValue) {
            // why is there a mismatch? return undefined instead of returning mismatched value
            return undefined
          }
          initialValue = foundInitialValue
          break
        }
        // fallback logic from Service_Order_Channel -> Service_Order -> Service -> RegularPrice
        const newFieldNameTokens = fieldNameToLookup.split("_")
        newFieldNameTokens.pop()
        fieldNameToLookup = newFieldNameTokens.join("_")
        if (!fieldNameToLookup) {
          // technically we should use regular price as fallback, but in this case regular price is already treated as a placeholder if value is missing
          break
        }
      }
    }
    return initialValue
  }

  const renderPriceField = (pricingConfigKey: string, channelKey?: string) => {
    const fieldName = getFieldNameForInputFromConfig(
      pricingConfigKey,
      channelKey,
    )
    const initialValue = findInitialValue(fieldName)
    const maxPriceDeviation =
      advancedPricingValidationConfig?.maxPriceDeviation ?? 0
    const maxPriceDifference =
      advancedPricingValidationConfig?.maxPriceDifference ?? 0
    const currencyCode =
      outletV2?.currency?.code || getCurrencyCodeByCountryCode(getUserCountry())

    return (
      <Form.Item
        required
        {...getValidationProps(form, [fieldName], true)[fieldName]}
        css={css`
          margin-bottom: 0;
        `}
      >
        {getFieldDecorator(fieldName, {
          getValueFromEvent: e => {
            if (fieldName === "regular") {
              setTimeout(
                () =>
                  form.validateFields({
                    force: true,
                  }),
                0,
              )
            }
            return eventValueToCleanNumericInput(
              e,
              getCurrencyInfo(currencyCode)?.decimal,
            )
          },
          initialValue,
          validateFirst: true,
          rules: [
            ...(pricingConfigKey === "regular"
              ? [
                  {
                    validator: priceValidator.onChangeRuleErrorClearerValidator,
                  },
                  {
                    validator: newPriceDiffValidator(
                      maxPriceDifference,
                      textMap.errorPriceDifference.replace(
                        /<limit>/i,
                        String(maxPriceDifference * 100),
                      ),
                      existingDefaultPrice,
                    ),
                  },
                ]
              : [
                  {
                    validator:
                      optionalPriceValidator.onChangeRuleErrorClearerValidator,
                  },
                  {
                    validator: newPriceDiffValidator(
                      maxPriceDeviation,
                      textMap.errorPriceDeviation.replace(
                        /<limit>/i,
                        String(maxPriceDeviation * 100),
                      ),
                      getFieldValue("regular"),
                    ),
                  },
                  ...getAdvancedPricingRules(
                    advancedPricingValidationConfig,
                    initialValue,
                    textMap,
                  ),
                ]),
          ],
        })(
          <Input
            autoComplete="off"
            data-testid={`input-${fieldName}`}
            addonBefore={storageHelper.getUserCurrency()}
            placeholder={getFieldValue("regular") || textMap.pricePlaceholder}
          />,
        )}
      </Form.Item>
    )
  }

  const handleClickApplyToAll = () => {
    const formValues = getFieldsValue()
    const formKeysToClear = Object.keys(formValues).filter(k => k !== "regular")
    const formValuesToSet = formKeysToClear.reduce(
      (acc, curr) => ({ ...acc, [curr]: "" }),
      {},
    )
    setFieldsValue(formValuesToSet)
    setIsEditedThroughAntd(true)
  }

  const handleClickConfirm = () => {
    form.validateFields(
      (formError: any, formValues: Record<string, string>) => {
        if (formError) return
        const didPriceValidationFailInAtLeastOneAdditionalPrice = Object.entries(
          formValues,
        )
          .filter(
            // if regular price OR value is NOT defined, skip validation
            ([k, v]) => k !== "regular" && v,
          )
          .some(([k, v]) => !optionalPriceValidator.validateOnSave(v, k))
        if (didPriceValidationFailInAtLeastOneAdditionalPrice) {
          return
        }
        if (!priceValidator.validateOnSave(formValues.regular, "regular")) {
          return
        }
        const cleanedFormValues = Object.entries(formValues).reduce<
          AdditionalPrice
        >(
          (acc, curr) => {
            const [formKey, formValue] = curr
            return {
              ...acc,
              [formKey]: formValue || null, // remove empty strings or empty vals
            }
          },
          {
            regular: null,
          },
        )
        onAdditionalPriceChange(cleanedFormValues)
        onClose()
      },
    )
  }

  return (
    <Drawer
      title={textMap.title}
      open={visible}
      onClose={onClose}
      width={450}
      destroyOnClose
      closeIcon={<ChevronLeftUniversal />}
    >
      <div css={drawerBodyStyle}>
        <div
          css={css`
            ${drawerContentStyle};
            margin-bottom: 8px;
          `}
        >
          <Row align="middle">
            <Col span={16}>
              <div
                css={css`
                  margin-bottom: 8px;
                `}
              >
                {textMap.regular}*
              </div>
              <Button
                type="link"
                data-testid="btn-apply-to-all"
                onClick={handleClickApplyToAll}
              >
                {textMap.applyToAll}
              </Button>
            </Col>
            <Col span={8}>{renderPriceField("regular")}</Col>
          </Row>
        </div>
        {isLoading ? (
          <div
            css={css`
              text-align: center;
            `}
          >
            <Spin />
          </div>
        ) : (
          <div css={drawerContentStyle}>
            {pricingConfig?.map((config, i) => (
              <Fragment key={config.key}>
                {!config.channels?.length ? (
                  <Fragment>
                    <Row align="middle">
                      <Col span={16}>
                        <div
                          css={css`
                            display: flex;
                            flex-direction: column;
                            white-space: pre-wrap;
                          `}
                        >
                          {getLocalisedName(config, locale, disallowedSOCKeys)}
                          {getFieldValue(
                            getFieldNameForInputFromConfig(
                              config.key,
                              // "Default",
                            ),
                          ) ? (
                            <CustomTag />
                          ) : null}
                        </div>
                      </Col>
                      <Col span={8}>{renderPriceField(config.key)}</Col>
                    </Row>
                  </Fragment>
                ) : (
                  <Fragment>
                    <div
                      css={css`
                        font-weight: 500;
                        text-transform: uppercase;
                        margin-bottom: 16px;
                        white-space: pre-wrap;
                      `}
                    >
                      {getLocalisedName(config, locale, disallowedSOCKeys)}
                    </div>
                    {config.channels?.map((channel, channelIdx) => (
                      <Fragment key={channel.key}>
                        <Row align="middle">
                          <Col span={16}>
                            <div
                              css={css`
                                display: flex;
                                flex-direction: column;
                              `}
                            >
                              {getLocalisedName(
                                {
                                  key: channel.key,
                                  name: {
                                    [channel.key]: {
                                      translation: channel.name,
                                    },
                                  },
                                },
                                locale,
                                disallowedSOCKeys,
                              )}
                              {getFieldValue(
                                getFieldNameForInputFromConfig(
                                  config.key,
                                  channel.key,
                                ),
                              ) ? (
                                <CustomTag />
                              ) : null}
                            </div>
                          </Col>
                          <Col span={8}>
                            {renderPriceField(config.key, channel.key)}
                          </Col>
                        </Row>
                        {channelIdx < config.channels?.length! - 1 && (
                          <Divider />
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                )}
                {i < pricingConfig.length! - 1 && <Divider />}
              </Fragment>
            ))}
          </div>
        )}
        <span css={drawerFooterSpacing} />
        <div css={drawerFooter}>
          <Button
            type="primary"
            block
            data-testid="btn-submit"
            onClick={handleClickConfirm}
            disabled={
              (!form.isFieldsTouched() && !isEditedThroughAntd) ||
              hasFormFieldsErrors(form.getFieldsError())
            }
          >
            {textMap.confirm}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export const AdditionalPriceDrawer = Form.create<AdditionalPriceDrawerProps>(
  {},
)(AdditionalPriceDrawerComponent)
